import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import useConfig from "../actions/Tenants/config/configHook";
import {
    updatePreferences,
    updatePreferencesLocally,
    User,
    UserPreferences,
} from "../actions/Users/actions";
import { useManagedOptionsHook } from "../components/Forms/useManagedOptionsHook";
import { useConnectivity } from "../offline/connectivityMonitor";
import { isOfflineChecksEnabled } from "../offline/db";
import { getFilledArrayOrDefault, isFilledArray } from "../utils";

const GLOBAL_FILTERS_ON_TABS = {
    FRUIT_TYPE: "fruit_type",
    FACILITY: "facility",
    ASSIGNED_TEAM: "assigned_team",
} as const;

export const useUserPreferences = (): [
  UserPreferences & {
    fruitTypes: string[];
    facilities: string[];
    assignedTeam: string[];
  },
  (data: UserPreferences) => void
] => {
    const user = useSelector<any>((state) => state.auth.user) as User;
    const dispatch = useDispatch();
    const config = useConfig();
    const { isOnline } = useConnectivity();
    const offline = isOfflineChecksEnabled(config) && !isOnline;

    // Which global filters are enabled for this tenant?
    // Default behavior historically enabled fruit type if not explicitly set.
    const enabledGlobalFilters = useMemo(
        () => getFilledArrayOrDefault(
            config.root_config.global_filters_on_tabs,
            [GLOBAL_FILTERS_ON_TABS.FRUIT_TYPE]
        ),
        [config.root_config.global_filters_on_tabs]
    );

    const isFilterEnabled = useMemo(() => ({
        fruitType: enabledGlobalFilters.includes(GLOBAL_FILTERS_ON_TABS.FRUIT_TYPE),
        facility: enabledGlobalFilters.includes(GLOBAL_FILTERS_ON_TABS.FACILITY),
        assignedTeam: enabledGlobalFilters.includes(GLOBAL_FILTERS_ON_TABS.ASSIGNED_TEAM),
    }), [enabledGlobalFilters]);

    const preferences = user.preferences || {};
    const tenant_id = user.tenant?.tenant_id;

    // Set default preferences for tenant if not available
    const tenantPreferences = tenant_id && preferences?.[tenant_id] ? preferences?.[tenant_id] : {};

    // Get default values from user's tenant_user
    const defaultFacility = user?.tenant_user?.facility ? [user.tenant_user.facility] : [];
    const defaultTeams = getFilledArrayOrDefault(user?.tenant_user?.teams);

    // Get available options
    const fruitTypeOptions = config.get_fruit_types();
    const facilityOptions = useManagedOptionsHook({ object: {}, name: "facility" });
    const teamOptions = useManagedOptionsHook({ object: {}, name: "teams" });

    // Filter fruit types based on available options
    const fruitTypes = useMemo(() => {
        if (!isFilterEnabled.fruitType) {
            return [];
        }
        if (isFilledArray(fruitTypeOptions)) {
            return getFilledArrayOrDefault(tenantPreferences?.fruit_types).filter(
                (fruitType) => fruitTypeOptions.find((option) => option.value === fruitType)
            );
        }
        return getFilledArrayOrDefault(tenantPreferences?.fruit_types);
    }, [fruitTypeOptions, tenantPreferences?.fruit_types, isFilterEnabled.fruitType]);

    // Filter facilities based on available options, use default if no preference is set
    const facilities = useMemo(() => {
        if (!isFilterEnabled.facility) {
            return [];
        }
        const raw = tenantPreferences?.facilities == null
            ? defaultFacility
            : getFilledArrayOrDefault(tenantPreferences?.facilities);
        if (isFilledArray(facilityOptions)) {
            return raw.filter(
                (facility) => facilityOptions.find((option) => option.value === facility)
            );
        }
        return raw;
    }, [facilityOptions, tenantPreferences?.facilities, defaultFacility, isFilterEnabled.facility]);

    // Filter assigned teams based on available options, use default if no preference is set
    const assignedTeam = useMemo(() => {
        if (!isFilterEnabled.assignedTeam) {
            return [];
        }
        const raw = tenantPreferences?.assigned_team == null
            ? defaultTeams
            : getFilledArrayOrDefault(tenantPreferences?.assigned_team);
        if (isFilledArray(teamOptions)) {
            return raw.filter(
                (team) => teamOptions.find((option) => option.value === team)
            );
        }
        return raw;
    }, [teamOptions, tenantPreferences?.assigned_team, defaultTeams, isFilterEnabled.assignedTeam]);

    const updateAllPreferences = (data: UserPreferences) => {
        if (tenant_id) {
            const nextPreferences = {
                ...preferences,
                [tenant_id]: {
                    ...tenantPreferences,
                    ...data,
                },
            };
            dispatch(offline ? updatePreferencesLocally(nextPreferences) : updatePreferences(nextPreferences));
        }
    };

    return [
        {
            ...tenantPreferences,
            fruitTypes,
            facilities,
            assignedTeam,
        },
        updateAllPreferences
    ];
};
